// Common helper functions for search functionality

/**
 * Get pagination element from DOM
 * @returns {HTMLElement|null} Pages element or null
 */
export function getPaginationElement() {
    try {
        // Pagination is now outside #nav-tabContent, find it as sibling
        const $tabContent = $('#nav-tabContent');
        const $pages = $tabContent.siblings('.pages').first();
        // Fallback: search globally if not found as sibling
        if (!$pages.length) {
            const $pagesGlobal = $('.pages').first();
            return $pagesGlobal.length ? $pagesGlobal[0] : null;
        }
        return $pages.length ? $pages[0] : null;
    } catch(_) {
        return null;
    }
}

/**
 * Clear pagination element completely
 * @param {boolean} debug - Enable debug logging
 */
export function clearPagination(debug = false) {
    const pagesElement = getPaginationElement();
    if (!pagesElement) return;
    
    // Remove legacy elements
    try {
        const $pages = $(pagesElement);
        $pages.find('.paginationjs').remove();
        $pages.find('.paginat').remove();
    } catch(_) {}
    
    // Nuclear clear
    while (pagesElement.firstChild) {
        pagesElement.removeChild(pagesElement.firstChild);
    }
    pagesElement.innerHTML = '';
}

/**
 * Get translation using TranslationRegistry
 * @param {string} key - Translation key
 * @param {string} defaultText - Default text if translation not found
 * @returns {string} Translated text
 */
export function getTranslation(key, defaultText = '') {
    try {
        const lang = (window.LANG || 'EN').toUpperCase();
        if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
            const translation = window.TranslationRegistry.translate(key, lang);
            if (translation !== key) return translation;
        }
    } catch(_) {}
    return defaultText || key;
}

/**
 * Show error state in UI
 * @param {string} errorText - Error message
 * @param {boolean} isEmptyQuery - Is this empty query error
 */
export function showSearchError(errorText, isEmptyQuery = false) {
    try {
        const $c = $('#nav-full-text');
        // Hide initial spinner when showing error
        const $initialSpinner = $c.find('.fulltext-initial-spinner');
        if ($initialSpinner.length) {
            $initialSpinner.addClass('d-none');
        }
        // Hide legacy spinner
        const $spinner = $c.find('.fulltext-loading-spinner');
        if ($spinner.length) {
            $spinner.addClass('d-none').hide();
        }
        $c.removeClass('loading');
        
        const $e = $c.find('.empty-content');
        $e.removeClass('d-none').find('.mb-1').text(errorText);
        
        // Update badge
        const cardRes = $('#nav-full-text-card-tab .card-results-count');
        if (cardRes.length) {
            cardRes.removeClass('card-results-found');
            const label = cardRes.find('div').first().html() || getTranslation('result_found', 'Found:') + ':';
            cardRes.html(`<div class="d-inline">${label}</div> 0`);
        }
    } catch(_) {}
}

/**
 * Get filesStore files array
 * @returns {Array} Files array
 */
export function getFilesStore() {
    try {
        return (window.filesStore && typeof window.filesStore.getFiles === 'function') 
            ? window.filesStore.getFiles() 
            : [];
    } catch(_) {
        return [];
    }
}

/**
 * Update results badge
 * @param {number} total - Total results count
 */
export async function updateResultsBadge(total) {
    try {
        const cardRes = $('#nav-full-text-card-tab .card-results-count');
        if (!cardRes.length) return;
        
        // Import state to check hasFulltextSearchBeenRun
        const stateModule = await import('./state.js');
        const hasFulltextSearchBeenRun = stateModule.getHasFulltextSearchBeenRun();
        
        if (total > 0 || (window.QUERY && typeof window.QUERY === 'string' && window.QUERY.trim().length > 0)) {
            stateModule.setHasFulltextSearchBeenRun(true);
        }
        
        if (total > 0) {
            cardRes.addClass('card-results-found').removeClass('text-subtle');
        } else {
            cardRes.removeClass('card-results-found');
        }
        
        let label = cardRes.find('div').first().html();
        if (!label) {
            label = getTranslation('result_found', 'Found:') + ':';
        }
        
        const displayValue = (!stateModule.getHasFulltextSearchBeenRun() && total === 0) ? '-' : total;
        cardRes.html(`<div class="d-inline">${label}</div> ${displayValue}`);
    } catch(_) {
        // Fallback if state module unavailable
        try {
            const cardRes = $('#nav-full-text-card-tab .card-results-count');
            if (cardRes.length) {
                if (total > 0) {
                    cardRes.addClass('card-results-found').removeClass('text-subtle');
                } else {
                    cardRes.removeClass('card-results-found');
                }
                let label = cardRes.find('div').first().html() || getTranslation('result_found', 'Found:') + ':';
                cardRes.html(`<div class="d-inline">${label}</div> ${total}`);
            }
        } catch(__) {}
    }
}

/**
 * Escape HTML to prevent XSS
 * @param {string} text - Text to escape
 * @returns {string} Escaped HTML
 */
export function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Format bytes to human-readable string
 * @param {number} bytes - Bytes to format
 * @returns {string} Formatted string (e.g., "1.5 MB")
 */
export function formatBytes(bytes) {
    try {
        if (!bytes || bytes <= 0) return '0 B';
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1000)));
        const val = bytes / Math.pow(1000, i);
        // Precision rule: <10 -> 1 decimal, >=10 -> 0 decimals
        const decimals = val < 10 ? 1 : 0;
        return `${val.toFixed(decimals)} ${units[i]}`;
    } catch(_) { return '0 B'; }
}

/**
 * Get effective file size in bytes from item or filesStore
 * @param {Object} item - Search result item
 * @returns {number} Size in bytes
 */
export function getEffectiveSizeBytes(item) {
    try {
        // Backend search results: item.s in KB (preferred), or item.size in KB (not bytes!)
        if (typeof item.s === 'number' && item.s > 0) return item.s * 1000;
        if (typeof item.size === 'number' && item.size > 0) return item.size * 1000;
        const link = (item.link || item.path || '').replace(/^\/+/, '').toLowerCase();
        const store = getFilesStore();
        if (store && store.length) {
            const found = store.find(f => {
                const full = `${(f.p || '').replace(/^\/+|\/+$/g, '')}${f.p ? '/' : ''}${f.n || ''}`.replace(/^\/+/, '').toLowerCase();
                return full === link;
            });
            // filesStore keeps 's' as KB
            if (found && typeof found.s === 'number' && found.s > 0) return found.s * 1000;
        }
    } catch(_) {}
    return 0;
}

